import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Smartphone, Download, Share, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallCard() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    // iOS detection
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Iframe detection (Lovable preview runs in iframe)
    try {
      setIsInIframe(window.self !== window.top);
    } catch (e) {
      setIsInIframe(true); // If access denied, we're in a cross-origin iframe
    }
  }, []);

  useEffect(() => {
    // Check if prompt was already captured globally
    if ((window as any).deferredPWAPrompt) {
      setDeferredPrompt((window as any).deferredPWAPrompt);
    }

    // Listen for the custom event dispatched when prompt becomes available
    const handlePromptAvailable = () => {
      if ((window as any).deferredPWAPrompt) {
        setDeferredPrompt((window as any).deferredPWAPrompt);
      }
    };

    // Also listen for direct beforeinstallprompt in case it fires after mount
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      (window as any).deferredPWAPrompt = null;
    };

    window.addEventListener('pwa-prompt-available', handlePromptAvailable);
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error installing PWA:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(window.location.origin + '/crm/settings', '_blank', 'noopener,noreferrer');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          App Installeren
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Iframe warning - shown when in Lovable preview */}
        {isInIframe && !isInstalled && (
          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/30">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <p className="font-medium mb-2">Installatie werkt niet in de preview</p>
              <p className="text-sm mb-3">
                Open de CRM in een nieuw tabblad om de app te kunnen installeren.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenInNewTab}
                className="border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-500 dark:text-amber-300 dark:hover:bg-amber-900/50"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open in nieuw tabblad
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {isInstalled ? (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                GreenVision CRM is geïnstalleerd
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                Je gebruikt de app al in standalone modus
              </p>
            </div>
          </div>
        ) : isIOS ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Installeer GreenVision CRM op je iPhone of iPad:
            </p>
            <ol className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium">1</span>
                <span>Tik op <Share className="inline h-4 w-4 mx-1" /> (Deel) onderaan</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium">2</span>
                <span>Scroll en kies "Zet op beginscherm"</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium">3</span>
                <span>Tik op "Voeg toe" rechtsboven</span>
              </li>
            </ol>
          </div>
        ) : deferredPrompt ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Installeer GreenVision CRM als desktop app voor snellere toegang.
            </p>
            <Button onClick={handleInstall} disabled={isInstalling} className="w-full sm:w-auto">
              <Download className="mr-2 h-4 w-4" />
              {isInstalling ? 'Installeren...' : 'Installeer als App'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
                Installeer via Chrome menu
              </p>
              <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>1. Klik op het menu-icoon (⋮) rechtsboven in Chrome</li>
                <li>2. Selecteer "GreenVision CRM installeren..." of "App installeren"</li>
                <li>3. Bevestig de installatie</li>
              </ol>
            </div>
            <p className="text-xs text-muted-foreground">
              De automatische installatie-prompt is niet beschikbaar. Dit kan komen doordat de app al geïnstalleerd is, 
              of doordat de browser de prompt niet ondersteunt.
            </p>
          </div>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs font-medium text-muted-foreground mb-2">Voordelen van de app:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Snellere laadtijden</li>
            <li>• Werkt offline (beperkt)</li>
            <li>• Eigen app icoon op je bureaublad</li>
            <li>• Volledig scherm ervaring</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
